<template>
  <b-card title="Health Checklist" sub-title="Overview of the store's health.">
    <b-list-group flush>
      <fact :cols="4" metric="Landscape">
        <b-row>
          <b-col :cols="2">             
            <traffic-light-icon :pass="passConnectionToLandscape.pass"/></b-col>
          <b-col>{{ passConnectionToLandscape.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="IBS tills">
        <b-row>
          <b-col :cols="2">             
            <traffic-light-icon :pass="passConnectionToIbs.pass"/></b-col>
          <b-col>{{ passConnectionToIbs.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="HQ server">
        <b-row>
          <b-col :cols="2"><traffic-light-icon :pass="passHqServer.pass"/></b-col>
          <b-col>{{ passHqServer.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="Event back-log">
        <b-row>
          <b-col :cols="2"><traffic-light-icon :pass="passNoBacklog.pass"/></b-col>
          <b-col>{{ passNoBacklog.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="DVR">
        <b-row>
          <b-col :cols="2">             
            <traffic-light-icon :pass="passDvrWebApp.pass"/></b-col>
          <b-col>{{ passDvrWebApp.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="Cameras">
        <b-row>
          <b-col :cols="2">             
            <traffic-light-icon :pass="passConnectionToCameras.pass"/></b-col>
          <b-col>{{ passConnectionToCameras.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="Service health">
        <b-row>
          <b-col :cols="2">             
            <traffic-light-icon :pass="passServicesHealthy.pass"/></b-col>
          <b-col>{{ passServicesHealthy.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="Time-Keeping">
        <b-row>
          <b-col :cols="2"><traffic-light-icon :pass="passTimeSyncWorking.pass"/></b-col>
          <b-col>{{ passTimeSyncWorking.description }}</b-col>
        </b-row>
      </fact>
      <fact :cols="4" metric="Service errors">
        <b-row>
          <b-col :cols="2"><traffic-light-icon :pass="passNoRecentErrors.pass"/></b-col>
          <b-col>{{ passNoRecentErrors.description }}</b-col>
        </b-row>
      </fact>
    </b-list-group>
  </b-card>
</template>

<script>
import Fact from './Fact.vue'
import TrafficLightIcon from './TrafficLightIcon.vue'
import * as moment from 'moment'

export default {
  props: ['recent', 'lastPing'],
  components: { Fact, TrafficLightIcon },
  computed: {
    landscapeWarning () {
      return { pass: 'unknown', description: "Unknown as store isn't talking to Landscape" }
    },
    localLastHeardFrom() {
      return moment(this.lastPing.raised_at_utc.value).add(this.lastPing.gmt_offset, 'minutes');
    },
    recentLandscape() {
      const lastHeardFromMins = moment.duration(moment().diff(this.localLastHeardFrom));
      const prettyLastHeardFromMins = lastHeardFromMins.humanize();
      return lastHeardFromMins.asMinutes() < 60
    },
    // HEALTH CHECKLIST
    // landscape
    passConnectionToLandscape() {
      const lastHeardFromMins = moment.duration(moment().diff(this.localLastHeardFrom));
      const prettyLastHeardFromMins = lastHeardFromMins.humanize();
      if (lastHeardFromMins.asMinutes() > 60) {
        return { pass: false, description: `Hmm.. not heard from store in ${prettyLastHeardFromMins}` };
      }
      return { pass: true, description: `Store made contact in the last ${prettyLastHeardFromMins}` }
    },
    // ibs tills
    passConnectionToIbs() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const noRecentNotifsErrors = this.recent.filter(r => r.report_type === 'M' && !r.is_resolved && r.message.indexOf('Last notification received ') === 0);
      if (noRecentNotifsErrors.length === 0) {
        return { pass: true, description: 'Transactions being received from IBS tills' };
      }
      return { pass: false, description: noRecentNotifsErrors[0].message };
    },
    // cameras
    passConnectionToCameras() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const cameraErrors = this.recent.filter(r => r.report_type === 'C' && !r.is_resolved);
      if (cameraErrors.length === 0) {
        return { pass: true, description: 'All cameras are available and streaming' };
      }
      return { pass: false, description: cameraErrors[0].message };
    },
    // service
    passServicesHealthy() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const serviceErrors = this.recent.filter(r => r.report_type === 'S' && !r.is_resolved);
      if (serviceErrors.length === 0) {
        return { pass: true, description: 'All required services are healthy' };
      }
      return { pass: false, description: serviceErrors[0].message };
    },
    // no errors
    passNoRecentErrors() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const errors = this.recent.filter(r => r.report_type !== 'P' && !r.is_resolved);
      if (errors.length === 0) {
        return { pass: true, description: 'No recent, unresolved errors from store' };
      }
      return { pass: false, description: `There are ${errors.length} unresolved errors` };
    },
    // timesync
    passTimeSyncWorking() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const errors = this.recent.filter(r => r.report_type === 'M' && (r.message||'').indexOf('TimeSync') !== -1 && !r.is_resolved);
      if (errors.length === 0) {
        return { pass: true, description: 'TimeSync appears to be working' };
      }
      return { pass: false, description: `TimeSync is not working which can lead to incorrectly timestamped transactions` };
    },
    // HQ server
    passHqServer() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const errors = this.recent.filter(r => r.report_type === 'M' && (r.message||'').indexOf('/VPReporting') !== -1 && !r.is_resolved);
      if (errors.length === 0) {
        return { pass: true, description: 'HQ Server is accessible to this store' };
      }
      return { pass: false, description: `Unable to reach HQ server at ${moment(errors[0].raised_at_utc.value).format("h:mm A")}` };
    },
    // DVR Web App
    passDvrWebApp() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const errors = this.recent.filter(r => r.report_type === 'M' && (r.message||'').indexOf('/doc/page/login.asp') !== -1 && !r.is_resolved);
      if (errors.length === 0) {
        return { pass: true, description: 'DVR Web App is accessible to the LiveStore Agent' };
      }
      return { pass: false, description: `Unable to reach DVR's Web App page at ${moment(errors[0].raised_at_utc.value).format("h:mm A")}` };
    },
    // Outgoing queue size
    passNoBacklog() {
      if (!this.recentLandscape) { return this.landscapeWarning }
      const errors = this.recent.filter(r => (r.message||'').indexOf('Queue was larger than threshold') !== -1 && !r.is_resolved);
      if (errors.length === 0) {
        return { pass: true, description: 'The store delivering notifications in near real-time' };
      }
      return { pass: false, description: 'There is currently a backlog of notifications of this store, possibly due to a networking issue' };
    },
  }
}
</script>
